import React, { useMemo } from 'react';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import CallMergeOutlinedIcon from '@mui/icons-material/CallMergeOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import CompressOutlinedIcon from '@mui/icons-material/CompressOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import ArcaPageShell from './components/ArcaPageShell';
import ToolGrid from '../../components/arca/ToolGrid';
import { useAuth } from '../../contexts/AuthContext';
import { canAccessArcaProcessosContratos } from './utils/arcaAccess';

const iconSx = { fontSize: 28 };

/** Catálogo ARCA PDF */
export const ARCA_PDF_CATEGORIES = [
  {
    id: 'processos',
    label: 'Processos',
    tools: [
      {
        id: 'processo-pagamento',
        name: 'Processo de pagamento',
        description: 'Monte o PDF do processo, envie ao financeiro e notifique o fiscal por e-mail.',
        path: '/arca/pdf/processo-pagamento',
        icon: <AccountBalanceWalletOutlinedIcon sx={iconSx} />,
        enabled: true,
        requiresProcessosContratos: true,
      },
      {
        id: 'processo-administrativo',
        name: 'Processo Administrativo',
        description:
          'Contratação e aditivo: checklist documental, pareceres (Técnico/Financeiro e Diretoria) e minuta editável com IA.',
        path: '/arca/pdf/processo-administrativo',
        icon: <GavelOutlinedIcon sx={iconSx} />,
        enabled: true,
        requiresProcessosContratos: true,
      },
    ],
  },
  {
    id: 'organizar',
    label: 'Organizar PDF',
    tools: [
      {
        id: 'juntar',
        name: 'Juntar PDF',
        description: 'Combine vários PDFs em um único arquivo, na ordem que definir.',
        path: '/arca/pdf/juntar',
        icon: <CallMergeOutlinedIcon sx={iconSx} />,
        enabled: true,
      },
      {
        id: 'dividir',
        name: 'Dividir PDF',
        description: 'Separe por intervalos ou gere um PDF por página.',
        path: '/arca/pdf/dividir',
        icon: <ContentCutOutlinedIcon sx={iconSx} />,
        enabled: true,
      },
      {
        id: 'organizar',
        name: 'Organizar PDF',
        description: 'Junte PDFs avulsos ao documento montado, reordene, exclua e gire páginas.',
        path: '/arca/pdf/organizar',
        icon: <ViewModuleOutlinedIcon sx={iconSx} />,
        enabled: true,
      },
    ],
  },
  {
    id: 'otimizar',
    label: 'Otimizar PDF',
    tools: [
      {
        id: 'comprimir',
        name: 'Comprimir PDF',
        description: 'Reduza o tamanho do arquivo com níveis de compressão.',
        path: '/arca/pdf/comprimir',
        icon: <CompressOutlinedIcon sx={iconSx} />,
        enabled: true,
      },
    ],
  },
  {
    id: 'converter-em',
    label: 'Converter em PDF',
    tools: [
      {
        id: 'jpg-pdf',
        name: 'JPG para PDF',
        description: 'Transforme imagens em um PDF único.',
        path: '/arca/pdf/jpg-para-pdf',
        icon: <ImageOutlinedIcon sx={iconSx} />,
        enabled: true,
      },
      {
        id: 'word-pdf',
        name: 'Word para PDF',
        description: 'Converta .docx em PDF preservando a formatação.',
        path: '/arca/pdf/word-para-pdf',
        icon: <DescriptionOutlinedIcon sx={iconSx} />,
        enabled: true,
      },
    ],
  },
  {
    id: 'converter-de',
    label: 'Converter de PDF',
    tools: [
      {
        id: 'pdf-jpg',
        name: 'PDF para JPG',
        description: 'Exporte cada página como imagem (ZIP se várias).',
        path: '/arca/pdf/pdf-para-jpg',
        icon: <PictureAsPdfOutlinedIcon sx={iconSx} />,
        enabled: true,
      },
      {
        id: 'pdf-word',
        name: 'PDF para Word',
        description: 'Gere um .docx editável a partir do PDF.',
        path: '/arca/pdf/pdf-para-word',
        icon: <DescriptionOutlinedIcon sx={iconSx} />,
        enabled: true,
      },
    ],
  },
];

export default function ArcaPdfPage() {
  const auth = useAuth();
  const canProcessos = canAccessArcaProcessosContratos(auth);

  const categories = useMemo(() => {
    return ARCA_PDF_CATEGORIES.map((cat) => ({
      ...cat,
      tools: cat.tools.filter((tool) => {
        if (tool.requiresProcessosContratos && !canProcessos) return false;
        return true;
      }),
    })).filter((cat) => cat.tools.length > 0);
  }, [canProcessos]);

  return (
    <ArcaPageShell
      title="ARCA PDF"
      subtitle="Ferramentas de PDF no servidor. Disponíveis: Juntar, Dividir, Organizar, Comprimir e JPG→PDF."
      maxWidth={1100}
    >
      <ToolGrid categories={categories} />
    </ArcaPageShell>
  );
}
